import * as Sentry from '@sentry/react';
import { isStaleBundleError } from './stale-bundle-guard';

// Glitchtip-compatible Sentry SDK init for the web CRM.
//
// We use Glitchtip (self-hosted) as the error tracker — it implements the
// Sentry ingest protocol so the same SDK works. DSN is a public-key URL,
// safe to ship in the client bundle.
//
// PII handling: this CRM holds real estate contacts and owners. Scrub
// anything that could leak PII before sending — Authorization headers,
// cookies, and the request body. Glitchtip is on our infrastructure, but
// belt-and-suspenders applies anyway.
export function initSentry(): void {
  const dsn = (import.meta.env.VITE_GLITCHTIP_DSN as string | undefined) || '';
  if (!dsn) {
    // No DSN configured (e.g. local dev) — silently skip. We don't need
    // a console warning every page load.
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_RELEASE as string | undefined) || undefined,
    // Capture all errors; sample 10% of transactions for tracing budget.
    tracesSampleRate: 0.1,
    // Don't track outgoing fetches to Supabase — too noisy and contains
    // user JWTs/tokens we don't want stored even on our own host.
    beforeSend(event, hint) {
      // Drop stale-bundle errors. The guard reloads the page automatically
      // on a deploy mismatch, and these aren't actionable bugs — they're
      // just noise from users holding old tabs.
      const errMessage =
        (hint?.originalException as { message?: string } | undefined)?.message
        ?? event.exception?.values?.[0]?.value
        ?? event.message;
      if (isStaleBundleError(errMessage)) {
        return null;
      }
      // Drop errors that originate inside Google Maps' minified library
      // (`/maps-api-v3/api/js/...`). They fire asynchronously when our map
      // component unmounts mid-init, after @vis.gl/react-google-maps has
      // already cleaned up its handlers — Google's internals still hold a
      // detached DOM ref and throw `getRootNode` / `b.keys`. The user never
      // sees these (the map is gone) and we can't intercept them; filter so
      // they stop polluting the dashboard.
      const exFrames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      // Sentry stores frames oldest-first, so the throw point is the last frame.
      const throwFrame = exFrames[exFrames.length - 1];
      if (throwFrame?.filename && /\/maps-api-v3\/api\/js\//.test(throwFrame.filename)) {
        return null;
      }
      // Strip auth + cookies from any captured HTTP request.
      if (event.request) {
        if (event.request.headers) {
          delete (event.request.headers as Record<string, string>)['Authorization'];
          delete (event.request.headers as Record<string, string>)['authorization'];
          delete (event.request.headers as Record<string, string>)['Cookie'];
          delete (event.request.headers as Record<string, string>)['cookie'];
        }
        if (event.request.cookies) {
          delete event.request.cookies;
        }
      }
      // Drop the request body — it can contain emails, phones, contact
      // details, message bodies. We have enough signal from the stack
      // trace + URL + breadcrumbs.
      if (event.request) {
        delete event.request.data;
      }
      // Don't capture the user's email in the user context — id only.
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
      }
      return event;
    },
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  });
}
